import { Text } from '@mantine/core';
import { useSelector } from 'react-redux';
import { useMultiForm } from '../../../../../../../react-commons/hooks/useMultiForm';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultVideoAttachmentContentResource,
    useVideoAttachmentContentActions,
    VideoAttachmentContentMutationResourceSchema,
} from '../../../../../resource/resources/artifacts/attachment/videoAttachmentContentResource';
import {
    defaultVideoAttachmentResource,
    useVideoAttachmentActions,
    VideoAttachmentMutationResourceSchema,
} from '../../../../../resource/resources/artifacts/attachment/videoAttachmentResource';
import { Form_EditableViewWrapper } from '../../../../reusable-components/form-components/Form_EditableViewWrapper';
import { ResourceValidationTitleBar } from '../../../../reusable-components/validation/ResourceValidationTitleBar';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { VideoAttachmentContent } from './VideoAttachmentContent';

export const VideoAttachmentView = ({ id }: { id: string | undefined }) => {
    const readOnly = useSelector(selectReadOnly);
    const validationScope = useSelector(selectNoScopeValidationScope);

    const videoAttachmentActions = useVideoAttachmentActions();
    const videoAttachmentContentActions = useVideoAttachmentContentActions();
    const videoAttachmentQuery = videoAttachmentActions.useGetResource(id);
    const videoAttachmentContentQuery = videoAttachmentContentActions.useGetSingleResource(id);
    const updateVideoAttachment = videoAttachmentActions.useUpdateResource();
    const updateVideoAttachmentContent = videoAttachmentContentActions.useUpdateResource();
    const addVideoAttachmentContent = videoAttachmentContentActions.useSetResource();
    const validateVideoAttachment = videoAttachmentActions.useValidateResource(id, validationScope);
    const validateVideoAttachmentContent = videoAttachmentContentActions.useValidateResource(
        videoAttachmentContentQuery.data?.id,
        validationScope
    );

    const { form: videoForm, formNames: videoFormNames } = useMyForm({
        schema: VideoAttachmentMutationResourceSchema,
        defaultValues: defaultVideoAttachmentResource,
        reactiveValues: videoAttachmentQuery.data,
    });
    const { form: videoContentForm, formNames: videoContentFormNames } = useMyForm({
        schema: VideoAttachmentContentMutationResourceSchema,
        defaultValues: defaultVideoAttachmentContentResource,
        reactiveValues: videoAttachmentContentQuery.data,
    });
    const { onSubmit, form } = useMultiForm(
        {
            primary: videoForm,
            secondary: videoContentForm,
        },
        async (data) => {
            if (videoForm.formState.isDirty) {
                await updateVideoAttachment.mutateAsync({
                    resource: data.primary,
                    id: data.primary.id,
                });
            }
            if (!data.secondary.id) {
                await addVideoAttachmentContent.mutateAsync({
                    resource: data.secondary,
                    parentId: data.primary.id,
                    withoutGrowl: true,
                });
            } else if (data.secondary.id && videoContentForm.formState.isDirty) {
                await updateVideoAttachmentContent.mutateAsync({
                    resource: data.secondary,
                    id: data.secondary.id,
                });
            }
        }
    );

    return (
        <QueryBaseComponent name="VideoAttachment" query={videoAttachmentQuery}>
            <Form_EditableViewWrapper form={form} onSubmit={onSubmit}>
                <ResourceValidationTitleBar
                    resourceValidationResult={
                        validateVideoAttachment.data && validateVideoAttachmentContent.data ?
                            [validateVideoAttachment.data, validateVideoAttachmentContent.data]
                        :   validateVideoAttachment.data
                    }
                    metaDataInfo={videoAttachmentQuery.data}
                    resourceType={[ResourceType.VIDEO_ATTACHMENT, ResourceType.VIDEO_ATTACHMENT_CONTENT]}
                >
                    <Text c="dimmed">Video Attachment</Text>
                </ResourceValidationTitleBar>
                <VideoAttachmentContent
                    form={videoForm}
                    formNames={videoFormNames}
                    contentForm={videoContentForm}
                    contentFormNames={videoContentFormNames}
                    id={id}
                    readOnly={readOnly}
                />
            </Form_EditableViewWrapper>
        </QueryBaseComponent>
    );
};
